import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Snackbar, Alert,
  IconButton, Divider, Avatar, CircularProgress,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { FormOption } from "../../api/requests/FormOptions";
import CampaignIcon from "@mui/icons-material/Campaign";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

const FORM_TYPE = "admin_notifications";
const FORM_FIELD = "messages";

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [formOptionId, setFormOptionId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await FormOption.getFormOptions(FORM_TYPE);
      const items = response?.data || (Array.isArray(response) ? response : []);
      if (items.length > 0) {
        const msgField = items.find((d) => d.form_field === FORM_FIELD);
        if (msgField) {
          setFormOptionId(msgField.id);
          const vals = msgField.options?.values || [];
          setNotifications(vals.map((v) => ({
            id: v.value,
            message: v.label,
            createdAt: v.createdAt || "",
          })));
        }
      }
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchNotifications(); }, []);

  const saveToApi = async (list) => {
    setSaving(true);
    const payload = {
      form_type: FORM_TYPE,
      form_field: FORM_FIELD,
      options: {
        values: list.map((n) => ({
          value: String(n.id),
          label: n.message,
          createdAt: n.createdAt,
        })),
      },
    };
    try {
      if (formOptionId) {
        await FormOption.updateFormOptions(formOptionId, payload);
      } else {
        const res = await FormOption.createFormOptions(payload);
        const newId = res?.data?.id || res?.id;
        if (newId) setFormOptionId(newId);
        else {
          await fetchNotifications();
        }
      }
      setNotifications(list);
      return true;
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to save. Please try again.", severity: "error" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newMessage.trim()) {
      setSnackbar({ open: true, message: "Please enter a notification message", severity: "warning" });
      return;
    }
    const newItem = {
      id: String(Date.now()),
      message: newMessage.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = [newItem, ...notifications];
    const ok = await saveToApi(updated);
    if (ok) {
      setNewMessage("");
      setSnackbar({ open: true, message: "Notification added for all users", severity: "success" });
    }
  };

  const handleDelete = async (id) => {
    const updated = notifications.filter((n) => n.id !== id);
    const ok = await saveToApi(updated);
    if (ok) {
      setSnackbar({ open: true, message: "Notification removed", severity: "info" });
    }
  };

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 800 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <CampaignIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 500, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Notifications
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 400 }}>
            Add scrolling notifications visible to all users on their dashboard.
          </Typography>
        </Box>

        {/* Add notification */}
        <Box sx={{
          backgroundColor: Colors.white, borderRadius: "12px",
          border: `1px solid ${Colors.dividerColor}`, p: 2.5, mb: 3,
        }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, mb: 1.5 }}>
            New notification
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              fullWidth size="small" placeholder="Type notification message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              disabled={saving}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px", fontSize: "14px",
                  "& fieldset": { borderColor: Colors.dividerColor },
                  "&:hover fieldset": { borderColor: Colors.primary },
                  "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
                },
              }}
            />
            <Button
              variant="contained" onClick={handleAdd} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                backgroundColor: Colors.primary, fontWeight: 500, fontSize: "13px",
                borderRadius: "10px", px: 3, textTransform: "none", boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: Colors.hoverColorBtn },
              }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Active notifications */}
        <Box sx={{
          backgroundColor: Colors.white, borderRadius: "12px",
          border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
        }}>
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor }}>
              Active notifications ({notifications.length})
            </Typography>
          </Box>
          <Divider sx={{ borderColor: Colors.dividerColor }} />

          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ color: Colors.primary }} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CampaignIcon sx={{ fontSize: 36, color: Colors.dividerColor, mb: 1 }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.secondaryColor }}>
                No active notifications
              </Typography>
            </Box>
          ) : (
            notifications.map((n, i) => (
              <Box key={n.id}>
                <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary, mt: 0.2 }}>
                    <CampaignIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.primaryTextColor, lineHeight: 1.5 }}>
                      {n.message}
                    </Typography>
                    {n.createdAt && (
                      <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor, mt: 0.3 }}>
                        {new Date(n.createdAt).toLocaleString("en-IN")}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small" onClick={() => handleDelete(n.id)} disabled={saving}
                    sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                {i < notifications.length - 1 && <Divider sx={{ borderColor: "#F1F5F9" }} />}
              </Box>
            ))
          )}
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminNotifications;
